import { Button } from "../../../components/ui/button";
import { Card, CardContent, CardHeader } from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { Progress } from "../../../components/ui/progress";
import { useAccount } from "wagmi";
import {
  Coins,
  Users,
  Clock,
  Twitter,
  Send,
  ExternalLink,
  ArrowRight,
  CheckCircle,
  Star,
  Trophy,
  Filter,
  Search,
  TwitterIcon,
  Instagram,
  SendIcon,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { DASHBOARD_PATH } from "@/constants/constant";
import { useEffect, useState } from "react";
import { useGetCampaignsApiCallQuery } from "@/services/api/campaigns/campaigns.api";
import { getTimeRemaining } from "@/utils/getTimeRemaining";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import { useGetTokenPriceConversionApiCallQuery } from "@/services/api/auth/auth.api";

const Tabs = [
  {
    id: 0,
    label: "All Campaigns",
  },
  {
    id: 1,
    label: "Active",
  },
  {
    id: 2,
    label: "Upcoming",
  },
];

export default function AirdropsSection() {
  const { isConnected } = useAccount();
  const navigate = useNavigate();
  const { openConnectModal } = useConnectModal();
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const [activeTab, setActiveTab] = useState("All Campaigns");
  const campaigns = [
    {
      id: 1,
      name: "BINGOLD Genesis Launch",
      description: "Join the official launch campaign for BINGOLD tokens",
      totalPool: "1,000,000",
      reward: "500",
      participants: "12,847",
      progress: 75,
      timeLeft: "5 days",
      tasks: [
        { platform: "twitter", completed: true },
        { platform: "telegram", completed: true },
        { platform: "discord", completed: false },
      ],
      status: "active",
      featured: true,
      href: DASHBOARD_PATH.ALL_CAMPAIGNS,
    },
    {
      id: 2,
      name: "Community Builder Campaign",
      description: "Earn rewards by building our community across platforms",
      totalPool: "500,000",
      reward: "250",
      participants: "8,932",
      progress: 100,
      timeLeft: "12 days",
      tasks: [
        { platform: "twitter", completed: true },
        { platform: "telegram", completed: true },
      ],
      status: "claimable",
      featured: false,
      href: DASHBOARD_PATH.ALL_CAMPAIGNS,
    },
    {
      id: 3,
      name: "Early Adopter Rewards",
      description: "Special rewards for early BINGOLD adopters",
      totalPool: "750,000",
      reward: "400",
      participants: "15,234",
      progress: 67,
      timeLeft: "3 days",
      tasks: [
        { platform: "twitter", completed: true },
        { platform: "telegram", completed: false },
        { platform: "discord", completed: true },
      ],
      status: "active",
      featured: false,
      href: DASHBOARD_PATH.ALL_CAMPAIGNS,
    },
    {
      id: 4,
      name: "Referral Program",
      description: "Invite friends and earn bonus BINGOLD tokens",
      totalPool: "300,000",
      reward: "150",
      participants: "6,721",
      progress: 0,
      timeLeft: "30 days",
      tasks: [
        { platform: "twitter", completed: false },
        { platform: "telegram", completed: false },
      ],
      status: "new",
      featured: false,
      href: DASHBOARD_PATH.ALL_CAMPAIGNS,
    },
    {
      id: 5,
      name: "Content Creator Program",
      description: "Create content and earn BINGOLD rewards",
      totalPool: "400,000",
      reward: "200",
      participants: "9,156",
      progress: 0,
      timeLeft: "25 days",
      tasks: [
        { platform: "twitter", completed: false },
        { platform: "telegram", completed: false },
        { platform: "discord", completed: false },
      ],
      status: "new",
      featured: false,
      href: DASHBOARD_PATH.ALL_CAMPAIGNS,
    },
    {
      id: 6,
      name: "Liquidity Provider Bonus",
      description: "Extra rewards for providing liquidity",
      totalPool: "600,000",
      reward: "350",
      participants: "4,892",
      progress: 0,
      timeLeft: "45 days",
      tasks: [
        { platform: "twitter", completed: false },
        { platform: "telegram", completed: false },
      ],
      status: "coming-soon",
      featured: false,
      href: DASHBOARD_PATH.ALL_CAMPAIGNS,
    },
  ];

  const { data: tokenPriceData } = useGetTokenPriceConversionApiCallQuery();

  const getUSDTBalance = (walletBalance: number) => {
    const usdtPrice =
      tokenPriceData?.result?.data?.find((item: any) => item.coin === "usdt")
        ?.tokenPrice || 0;
    const balance = walletBalance * usdtPrice || 0;
    return balance;
  };

  const { data: campaignsData, isError,refetch } = useGetCampaignsApiCallQuery({
    campaignStatus: activeTab === "All Campaigns" ? "" : activeTab ,
  });


 useEffect(() => {
    refetch(); // Force API call on tab change
  }, [activeTab, refetch]);
  const handleCampaignClick = () => {
    if (isConnected) {
      navigate(DASHBOARD_PATH.ALL_CAMPAIGNS);
    } else {
      if (openConnectModal) {
        setShouldRedirect(true);
        openConnectModal();
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "claimable":
        return "bg-green-500";
      case "Active":
        return "bg-blue-500";
      case "new":
        return "bg-yellow-500";
      case "coming-soon":
        return "bg-gray-500";
      default:
        return "bg-gray-500";
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "claimable":
        return "Ready to Claim";
      case "active":
        return "Active";
      case "new":
        return "New";
      case "coming-soon":
        return "Coming Soon";
      default:
        return "Unknown";
    }
  };

  const getButtonText = (status: string, progress: number) => {
    if (status === "claimable") return "Claim Rewards";
    if (status === "coming-soon") return "Notify Me";
    if (progress > 0) return "Continue Tasks";
    return "Start Campaign";
  };

  const getButtonStyle = (status: string) => {
    if (status === "claimable") {
      return "bg-gradient-to-r from-green-400 to-green-600 text-black hover:from-green-500 hover:to-green-700";
    }
    if (status === "coming-soon") {
      return "bg-gray-600 text-white hover:bg-gray-700 cursor-not-allowed";
    }
    return "bg-gradient-to-r from-yellow-400 to-yellow-600 text-black hover:from-yellow-500 hover:to-yellow-700";
  };

  const getIcons = (icon: String) => {
    switch (icon) {
      case "Twitter":
        return <TwitterIcon size={15} />;
      case "Telegram":
        return <SendIcon size={15} />;
      case "Instagram":
        return <Instagram size={15} />;
      default:
        return "";
    }
  };

  useEffect(() => {
    if (isConnected && shouldRedirect) {
      navigate("/dashboard");
      setShouldRedirect(false); // reset so it doesn't keep redirecting
    }
  }, [isConnected, shouldRedirect]);

  const handleTabChange = (tabLabel: string) => {
    console.log("🔄 Switching to tab:", tabLabel); // Debug log
    setActiveTab(tabLabel);

    // Optional: Show loading state or reset any filters
    // setSearchQuery("");
  };

  return (
    <section
      id="airdrop"
      className="py-20 text-white bg-[linear-gradient(-245deg,_#0d1523,_#050914,_#000000)]"
    >
      <div className="container mx-auto px-4 md:px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl mb-6">
            BINGOLD{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-600">
              Campaigns
            </span>
          </h2>
          <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto">
            Complete social media tasks and earn your share of BINGOLD tokens.
            Each campaign offers unique rewards and requirements.
          </p>
        </div>

        {/* Cards */}
        {/* <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="border border-yellow-500/20 rounded-2xl p-6 flex justify-center items-center bg-[#0C1220]">
            <div>
              <div className="flex flex-col items-center gap-2">
                <span className="size-12 bg-gradient-to-tr from-yellow-400 to-yellow-600 rounded-lg flex items-center justify-center text-black font-medium">
                  <Trophy />
                </span>

                <h2>2.0M</h2>
                <p>Total BINGOLD Pool</p>
              </div>
            </div>
          </div>

          <div className="border border-yellow-500/20 rounded-2xl p-6 flex justify-center items-center bg-[#0C1220]">
            <div>
              <div className="flex flex-col items-center gap-2">
                <span className="size-12 bg-gradient-to-tr from-yellow-400 to-yellow-600 rounded-lg flex items-center justify-center text-black font-medium">
                  <Trophy />
                </span>

                <h2>2.0M</h2>
                <p>Total BINGOLD Pool</p>
              </div>
            </div>
          </div>

          <div className="border border-yellow-500/20 rounded-2xl p-6 flex justify-center items-center bg-[#0C1220]">
            <div>
              <div className="flex flex-col items-center gap-2">
                <span className="size-12 bg-gradient-to-tr from-yellow-400 to-yellow-600 rounded-lg flex items-center justify-center text-black font-medium">
                  <Trophy />
                </span>

                <h2>2.0M</h2>
                <p>Total BINGOLD Pool</p>
              </div>
            </div>
          </div>
        </div> */}

        {/* Filters */}
        <div className="flex flex-col md:flex-row items-center gap-4 mt-12">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search campaigns..."
                className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500"
              />
            </div>
          </div>
          {/* 
          <Button
            variant="outline"
            className="border-yellow-600 text-gray-300 hover:bg-gray-300"
          >
            <Filter className="w-4 h-4 mr-2" />
            Filter By Category
          </Button> */}
        </div>

        {/* Tabs  */}

        <div
          className="flex items-center justify-between gap-8 mt-8 bg-gray-800 rounded-md px-2 py-1"
          id="campaigns"
        >
          {Tabs.map((tab, index) => {
            const isActiveTab = activeTab === tab.label;
            return (
              <div
                key={tab.id}
                className={`w-full rounded-full flex items-center justify-center gap-2 cursor-pointer py-1 font-medium ${
                  isActiveTab ? "text-gray-700 bg-yellow-500" : "text-white"
                }`}
                onClick={() => handleTabChange(tab.label)}
              >
                <p>{tab.label}</p>
              </div>
            );
          })}
        </div>

        {/* Token Info */}
        {/* <div className="bg-gradient-to-r from-yellow-400/10 to-yellow-600/10 border border-yellow-500/20 rounded-2xl p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            <div>
              <div className="text-2xl font-bold text-yellow-400 mb-2">
                BINGOLD
              </div>
              <div className="text-gray-400">Token Symbol</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-yellow-400 mb-2">BSC</div>
              <div className="text-gray-400">Blockchain</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-yellow-400 mb-2">1B</div>
              <div className="text-gray-400">Total Supply</div>
            </div>
          </div>
        </div> */}

        {/* Campaign Grid */}
        <div className="mt-14">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 ">
            {campaignsData?.result &&
              campaignsData?.result?.docs?.length > 0 &&
              campaignsData?.result?.docs.map((campaign) => {
                const { days, hours, minutes } = getTimeRemaining(
                  campaign.startDate,
                  campaign.endDate
                );
                return (
                  <Card
                    key={campaign._id}
                    className={`bg-gray-900/50 border-yellow-500/20 hover:border-yellow-500/40 transition-all duration-300 group ${
                      true ? "ring-2 ring-yellow-500/30" : ""
                    }`}
                  >
                    <CardHeader className="pb-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h3 className="text-white text-lg font-semibold">
                              {campaign.name}
                            </h3>
                            {/* {campaign.featured && (
                          <Star className="w-4 h-4 text-yellow-400 fill-current" />
                        )} */}
                          </div>
                          <p className="text-gray-400 text-sm leading-relaxed">
                            {campaign.description}
                          </p>
                        </div>
                      </div>

                      <Badge
                        className={`${getStatusColor(
                          campaign.status
                        )} text-white w-fit`}
                      >
                        {/* {getStatusText(campaign.status)} */}
                        {campaign.campaignStatus}
                      </Badge>
                    </CardHeader>

                    <CardContent className="space-y-4">
                      {/* Reward Info */}
                      <div className="grid grid-cols-2 gap-4 text-center">
                        <div className="bg-gray-800/50 rounded-lg p-3">
                          <div className="text-yellow-400 font-semibold">
                            {campaign.airdropAmount}
                          </div>
                          <div className="text-gray-400 text-xs">
                            BINGOLD Reward
                          </div>
                        </div>
                        <div className="bg-gray-800/50 rounded-lg p-3">
                          <div className="text-white font-semibold">
                            {campaign.totalJoined}
                          </div>
                          <div className="text-gray-400 text-xs">
                            Participants
                          </div>
                        </div>
                      </div>

                      {/* Progress (only for active campaigns) */}
                      {campaign.status !== "coming-soon" && (
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-400">Your Progress</span>
                            <span className="text-white">
                              {campaign.progress}% Complete
                            </span>
                          </div>
                          <Progress
                            value={campaign.progress}
                            className="h-2 bg-gray-800"
                          >
                            <div
                              className="h-full bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-full transition-all"
                              style={{ width: `${campaign.progress}%` }}
                            />
                          </Progress>
                        </div>
                      )}

                      {/* Social Platforms */}
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400 text-sm">Worth $ </span>
                        <span>
                          {" "}
                          $
                          {Number(
                            getUSDTBalance(campaign.airdropAmount)
                          ).toFixed(2)}{" "}
                        </span>
                        {/* <div className="flex space-x-1">
                          {campaign?.platforms?.map((platform) => (
                            <div
                              key={platform}
                              className={`w-6 h-6 rounded-full flex items-center justify-center`}
                            >
                              {getIcons(platform)}
                            </div>
                          ))}
                        
                        </div> */}
                      </div>

                      {/* Time Left */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2 text-gray-400">
                          <Clock className="w-4 h-4" />
                          <span className="text-sm">
                            {days}d {hours}h {minutes}m left
                          </span>
                        </div>
                        {/* <div className="text-gray-400 text-sm">
                      Pool: {campaign.totalPool} BINGOLD
                    </div> */}
                      </div>

                      {/* Action Button */}
                      <div onClick={() => handleCampaignClick()}>
                        <Button
                          className={`w-full cursor-pointer ${getButtonStyle(
                            campaign.status
                          )}`}
                          disabled={campaign.status === "coming-soon"}
                        >
                          {getButtonText(campaign.status, campaign.progress)}
                          {campaign.status !== "coming-soon" && (
                            <ArrowRight className="w-4 h-4 ml-2" />
                          )}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
          </div>

          {isError ||
            (campaignsData?.result?.docs.length === 0 && (
              <div className="text-center text-gray-400 mt-4">
                No campaigns found.
              </div>
            ))}
        </div>

        {/* Call to Action */}
        {!isConnected && (
          <div className="text-center mt-14">
            <div className="bg-gradient-to-r from-yellow-400/10 to-yellow-600/10 border border-yellow-500/20 rounded-2xl p-8 w-full mx-auto">
              <h3 className="text-2xl font-bold text-white mb-4">
                Ready to Start Earning?
              </h3>
              <p className="text-gray-300 mb-6">
                Connect your wallet to participate in BINGOLD campaigns and
                start earning tokens today.
              </p>
              <Button
                onClick={handleCampaignClick}
                className="cursor-pointer bg-gradient-to-r from-yellow-400 to-yellow-600 text-black hover:from-yellow-500 hover:to-yellow-700 px-8 py-3"
              >
                Connect Wallet & Start
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

//  {campaigns.map((campaign) => (
//             <Card
//               key={campaign.id}
//               className={`bg-gray-900/50 border-yellow-500/20 hover:border-yellow-500/40 transition-all duration-300 group ${
//                 campaign.featured ? "ring-2 ring-yellow-500/30" : ""
//               }`}
//             >
//               <CardHeader className="pb-4">
//                 <div className="flex items-start justify-between mb-3">
//                   <div className="flex-1">
//                     <div className="flex items-center space-x-2 mb-2">
//                       <h3 className="text-white text-lg font-semibold">
//                         {campaign.name}
//                       </h3>
//                       {campaign.featured && (
//                         <Star className="w-4 h-4 text-yellow-400 fill-current" />
//                       )}
//                     </div>
//                     <p className="text-gray-400 text-sm leading-relaxed">
//                       {campaign.description}
//                     </p>
//                   </div>
//                 </div>

//                 <Badge
//                   className={`${getStatusColor(
//                     campaign.status
//                   )} text-white w-fit`}
//                 >
//                   {getStatusText(campaign.status)}
//                 </Badge>
//               </CardHeader>

//               <CardContent className="space-y-4">
//                 {/* Reward Info */}
//                 <div className="grid grid-cols-2 gap-4 text-center">
//                   <div className="bg-gray-800/50 rounded-lg p-3">
//                     <div className="text-yellow-400 font-semibold">
//                       {campaign.reward}
//                     </div>
//                     <div className="text-gray-400 text-xs">BINGOLD Reward</div>
//                   </div>
//                   <div className="bg-gray-800/50 rounded-lg p-3">
//                     <div className="text-white font-semibold">
//                       {campaign.participants}
//                     </div>
//                     <div className="text-gray-400 text-xs">Participants</div>
//                   </div>
//                 </div>

//                 {/* Progress (only for active campaigns) */}
//                 {campaign.status !== "coming-soon" && (
//                   <div className="space-y-2">
//                     <div className="flex justify-between text-sm">
//                       <span className="text-gray-400">Your Progress</span>
//                       <span className="text-white">
//                         {campaign.progress}% Complete
//                       </span>
//                     </div>
//                     <Progress
//                       value={campaign.progress}
//                       className="h-2 bg-gray-800"
//                     >
//                       <div
//                         className="h-full bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-full transition-all"
//                         style={{ width: `${campaign.progress}%` }}
//                       />
//                     </Progress>
//                   </div>
//                 )}

//                 {/* Social Platforms */}
//                 <div className="flex items-center space-x-2">
//                   <span className="text-gray-400 text-sm">Platforms:</span>
//                   <div className="flex space-x-2">
//                     {campaign.tasks.map((task, index) => {
//                       const Icon =
//                         task.platform === "twitter"
//                           ? Twitter
//                           : task.platform === "telegram"
//                           ? Send
//                           : Users;
//                       return (
//                         <div
//                           key={index}
//                           className={`w-6 h-6 rounded-full flex items-center justify-center ${
//                             task.completed ? "bg-green-500" : "bg-gray-600"
//                           }`}
//                         >
//                           <Icon className="w-3 h-3 text-white" />
//                         </div>
//                       );
//                     })}
//                   </div>
//                 </div>

//                 {/* Time Left */}
//                 <div className="flex items-center justify-between">
//                   <div className="flex items-center space-x-2 text-gray-400">
//                     <Clock className="w-4 h-4" />
//                     <span className="text-sm">{campaign.timeLeft} left</span>
//                   </div>
//                   <div className="text-gray-400 text-sm">
//                     Pool: {campaign.totalPool} BINGOLD
//                   </div>
//                 </div>

//                 {/* Action Button */}
//                 <Link to={campaign.href}>
//                   <Button
//                     className={`w-full cursor-pointer ${getButtonStyle(
//                       campaign.status
//                     )}`}
//                     disabled={campaign.status === "coming-soon"}
//                   >
//                     {getButtonText(campaign.status, campaign.progress)}
//                     {campaign.status !== "coming-soon" && (
//                       <ArrowRight className="w-4 h-4 ml-2" />
//                     )}
//                   </Button>
//                 </Link>
//               </CardContent>
//             </Card>
//           ))}
